import { useRef, useState, useEffect, useCallback } from 'react'
import { Plus, Minus } from 'lucide-react'
import { Settings, getIdentifierHistory } from '../api'
import { BoosterSectionName, getBoosterFieldsForSection, isBoosterFieldVisible, isAutopilotActive, ResourceDef, BOOSTER_RESOURCES, getApiHiddenSubsections, getApiHiddenFields } from '../resources_booster'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { CustomRegionsField } from './CustomRegionsField'
import { SearchUrlWeightsField } from './SearchUrlWeightsField'
import ProxyPresetsPanel from './ProxyPresetsPanel'

interface Props {
  section: BoosterSectionName
  cfg: Settings
  update: (patch: Partial<Settings>) => void
  uploadBlock?: React.ReactNode
  isAdmin?: boolean
  campaignId?: string
}

// Idea #29: кнопка «Рассчитать» + панель с ETA по оставшимся
// заданиям. Дёргает /api/booster/campaigns/{cid}/optimal_threads.
// Bug #106: hint + кнопка раньше были в одном column-контейнере INSIDE
// flex-row родителя → при появлении hint (после клика «Рассчитать»)
// сам компонент рос вниз, а кнопка прыгала вверх (items-center
// центрировал общий «толстый» блок). Решение — разделить кнопку и
// info-panel: кнопка остаётся inline в row CNT_THREADS, info-panel
// отдельным block-элементом ПОД row. Используем общий React state
// через простой singleton-ref внутри hook, чтобы Info-panel могла
// читать данные которые Button получил.
const _optimalThreadsBus: { value: any; subs: Set<() => void> } = { value: null, subs: new Set() }
function _setOptimalThreadsValue(v: any) {
  _optimalThreadsBus.value = v
  _optimalThreadsBus.subs.forEach(f => f())
}
function _useOptimalThreadsValue(): any {
  const [, setN] = useState(0)
  useEffect(() => {
    const fn = () => setN(n => n + 1)
    _optimalThreadsBus.subs.add(fn)
    return () => { _optimalThreadsBus.subs.delete(fn) }
  }, [])
  return _optimalThreadsBus.value
}

function _fmtEta(sec: number): string {
  if (sec < 0) return '∞'
  if (sec === 0) return '—'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (h >= 24) return `${Math.floor(h / 24)}д ${h % 24}ч`
  if (h >= 1) return `${h}ч ${m}м`
  if (m >= 1) return `${m}м`
  return `${sec}с`
}

function OptimalThreadsButton({ campaignId, onApply }: { campaignId?: string; onApply: (n: number) => void }) {
  const [loading, setLoading] = useState(false)
  const data = _useOptimalThreadsValue()
  const [error, setError] = useState<string>('')

  const calc = async () => {
    if (!campaignId) {
      setError('Сначала сохраните проект')
      return
    }
    setLoading(true)
    setError('')
    _setOptimalThreadsValue(null)
    try {
      const r = await fetch(`/api/booster/campaigns/${campaignId}/optimal_threads`).then(r => r.json())
      if (r?.error) setError(String(r.error).slice(0, 200))
      else _setOptimalThreadsValue(r)
    } catch (e: any) {
      setError(e?.message || 'unknown error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center gap-2 shrink-0">
      <Button type="button" variant="outline" size="sm" disabled={loading} onClick={calc}>
        {loading ? '…' : 'Рассчитать'}
      </Button>
      {data && typeof data.optimal_threads === 'number' && data.optimal_threads !== data.current_threads && (
        <Button type="button" variant="secondary" size="sm" onClick={() => onApply(data.optimal_threads)}>
          → {data.optimal_threads} потоков
        </Button>
      )}
      {error && (
        <span className="text-[11px] text-rose-500 max-w-[16rem] truncate" title={error}>Ошибка: {error}</span>
      )}
    </div>
  )
}

// Info-panel рендерится ОТДЕЛЬНО, ниже row CNT_THREADS — не влияет
// на layout кнопки/степпера.
function OptimalThreadsInfoPanel() {
  const data = _useOptimalThreadsValue()
  if (!data) return null
  return (
    <div className="mb-2 mx-4 px-3 py-2 rounded-md bg-muted/40 border border-border/50 text-[12px] leading-relaxed text-muted-foreground space-y-1">
      <div className="text-foreground">{String(data.comment || '').slice(0, 240)}</div>
      {(data.tasks_total > 0 || data.tasks_unlimit) && (
        <div>
          Осталось заданий: <strong className="text-foreground">{
            data.tasks_unlimit
              ? '∞'
              : (data.tasks_remaining > 0
                  ? data.tasks_remaining.toLocaleString('ru')
                  : (data.tasks_total > 0 ? `~${data.tasks_total.toLocaleString('ru')} (проект ещё не запускался)` : '0'))
          }</strong>
          {!data.tasks_unlimit && data.tasks_remaining > 0 && (
            <> из <strong className="text-foreground">{data.tasks_total.toLocaleString('ru')}</strong></>
          )}
        </div>
      )}
      {(data.tasks_remaining > 0 || (data.tasks_total > 0 && data.tasks_remaining === 0)) && !data.tasks_unlimit && (
        <div>
          ETA при {data.current_threads} потоках: <strong className="text-foreground">{
            _fmtEta(data.eta_seconds_at_current > 0 ? data.eta_seconds_at_current : (data.tasks_total * data.avg_iter_sec / Math.max(1, data.current_threads)))
          }</strong>
          {data.optimal_threads !== data.current_threads && (
            <> · при {data.optimal_threads}: <strong className="text-emerald-500">{
              _fmtEta(data.eta_seconds_at_optimal > 0 ? data.eta_seconds_at_optimal : (data.tasks_total * data.avg_iter_sec / Math.max(1, data.optimal_threads)))
            }</strong></>
          )}
        </div>
      )}
    </div>
  )
}

function HintTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-xs font-bold shrink-0 border border-border text-muted-foreground hover:text-primary hover:border-primary/30 hover:bg-primary/10 transition-all"
          >
            ?
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[280px] text-xs leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function CustomDropdown({ options, value, onChange }: {
  options: string[]
  value: string
  onChange: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close()
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', esc)
    }
  }, [open, close])

  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector('[data-active="true"]') as HTMLElement
      if (active) active.scrollIntoView({ block: 'nearest' })
    }
  }, [open])

  const display = value || '\u2014'

  return (
    <div ref={ref} className="relative w-full max-w-80">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm text-left transition-all bg-background border text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${open ? 'border-ring' : 'border-input'}`}
      >
        <span className="truncate">{display}</span>
        <svg
          width="12" height="12" viewBox="0 0 12 12" fill="none"
          className={`shrink-0 transition-transform duration-150 text-muted-foreground ${open ? 'rotate-180' : ''}`}
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div
          ref={listRef}
          className="absolute left-0 right-0 z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md py-1 shadow-lg animate-fade-in bg-popover border border-border"
        >
          {options.map(opt => {
            const selected = opt === value
            const label = opt || '\u2014'
            return (
              <button
                key={opt}
                type="button"
                data-active={selected}
                onClick={() => { onChange(opt); close() }}
                className={`w-full text-left px-3 py-1.5 text-sm transition-colors truncate ${selected ? 'text-primary bg-primary/10' : 'text-foreground hover:bg-accent'}`}
              >
                {label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SelectField({ def, value, onChange }: {
  def: ResourceDef
  value: any
  onChange: (v: any) => void
}) {
  const opts = def.options ?? []
  const isCheckbox = opts.length === 2 && opts[0] === '\u0414\u0430' && opts[1] === ''

  if (isCheckbox) {
    const checked = String(value ?? def.defaultValue) === '\u0414\u0430'
    return (
      <div className="flex items-center gap-2.5">
        <Switch checked={checked} onCheckedChange={v => onChange(v ? '\u0414\u0430' : '')} />
        <span className={`text-xs ${checked ? 'text-foreground' : 'text-muted-foreground'}`}>
          {checked ? '\u0414\u0430' : '\u041d\u0435\u0442'}
        </span>
      </div>
    )
  }

  return (
    <CustomDropdown
      options={opts}
      value={String(value ?? def.defaultValue)}
      onChange={onChange}
    />
  )
}

// Bug #252 (smartwellsup/artstyle): длинные прокси (user:pass@host:port или
// socks5://…, строка >80 символов) в исходнике (терминал/чат/почта) часто
// обёрнуты РЕАЛЬНЫМ переносом → 200 прокси приходят как 400+ строк, хвост
// пароля определяется как отдельная «прокси» → auth 401. Наш импорт читал файл
// как есть. Нормализатор склеивает обрывки: строка, которая не начинается как
// валидная прокси, приклеивается к предыдущей (это перенесённый хвост пароля).
function _isProxyLineStart(s: string): boolean {
  const t = s.trim()
  if (!t) return false
  if (/^(socks[45]?|https?):\/\/\S/i.test(t)) return true         // socks5://… / http://…
  if (/^[^\s@:]+:[^\s@]*@[a-z0-9.\-]+:\d{2,5}\b/i.test(t)) return true  // user:pass@host:port (: перед @)
  if (/^[a-z0-9.\-]+:\d{2,5}(:|$)/i.test(t)) return true          // host:port[:user:pass]
  return false
}
function normalizeProxyImport(text: string): { text: string; joined: number } {
  const raw = text.split(/\r\n|\r|\n/)
  const out: string[] = []
  let joined = 0
  for (const ln of raw) {
    if (!ln.trim()) continue
    if (out.length > 0 && !_isProxyLineStart(ln)) {
      out[out.length - 1] += ln.trim()   // перенесённый хвост пароля → к предыдущей
      joined++
    } else {
      out.push(ln.trim())
    }
  }
  return { text: out.join('\n'), joined }
}

function LinesFromFileField({ name, value, description, onChange }: {
  name: string
  value: any
  description: string
  onChange: (v: string) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [joinNote, setJoinNote] = useState<number>(0)

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      let text = (ev.target?.result as string) ?? ''
      // Только для списка прокси: чиним перенесённые длинным паролем строки.
      if (name === 'PROXY_LIST') {
        const norm = normalizeProxyImport(text)
        text = norm.text
        setJoinNote(norm.joined)
      }
      onChange(text)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  return (
    <div className="space-y-1.5 w-full min-w-0 max-w-full md:max-w-md">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          className="gap-1.5 text-xs"
        >
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
          </svg>
          Загрузить файл
        </Button>
        <input ref={fileRef} type="file" accept=".txt,.csv,.list" className="hidden" onChange={handleFile}/>
        {value && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {String(value).split('\n').filter(Boolean).length} строк
          </span>
        )}
        {name === 'PROXY_LIST' && joinNote > 0 && (
          <span className="text-xs text-amber-500" title="Прокси были перенесены длинным паролем — склеены при импорте">
            склеено перенесённых: {joinNote}
          </span>
        )}
      </div>
      <Textarea
        value={String(value ?? '')}
        onChange={e => onChange(e.target.value)}
        rows={4}
        placeholder={`${description}\n(по одному значению на строку)`}
        className="w-full resize-y min-h-[80px] text-sm"
      />
    </div>
  )
}

function FilesFromDirectoryField({ value, description, onChange }: {
  value: any
  description: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-col gap-2 min-w-0 md:flex-row md:items-center">
      <Input
        type="text"
        value={String(value ?? '')}
        onChange={e => onChange(e.target.value)}
        placeholder={`Путь к папке (${description})`}
        className="w-full max-w-80 text-sm min-w-0"
      />
      <Badge variant="secondary" className="text-xs shrink-0 w-fit">DIR</Badge>
    </div>
  )
}

function MultiCheckboxField({ def, value, onChange }: {
  def: ResourceDef
  value: any
  onChange: (v: string) => void
}) {
  const opts = def.options ?? []
  const currentStr = String(value ?? '')
  const active = new Set(currentStr.split(',').map(s => s.trim()).filter(Boolean))

  const toggle = (opt: string) => {
    const next = new Set(active)
    if (next.has(opt)) next.delete(opt)
    else next.add(opt)
    onChange(Array.from(next).join(','))
  }

  return (
    <div className="flex flex-col gap-2 w-full max-w-md">
      {opts.map(opt => {
        const checked = active.has(opt)
        return (
          <label
            key={opt}
            htmlFor={`mc-${opt}`}
            className={`group flex items-center gap-3 rounded-lg border px-3.5 py-2.5 cursor-pointer transition-colors select-none
              ${checked
                ? 'border-primary/40 bg-primary/5 hover:bg-primary/10'
                : 'border-border bg-card hover:bg-accent/40'}`}
          >
            <Checkbox
              id={`mc-${opt}`}
              checked={checked}
              onCheckedChange={() => toggle(opt)}
              className="shrink-0"
            />
            <span className={`flex-1 text-sm ${checked ? 'text-foreground font-medium' : 'text-foreground/80'}`}>
              {opt}
            </span>
          </label>
        )
      })}
    </div>
  )
}

function IdentifierWithHistory({ fieldKey, value, placeholder, onChange }: {
  fieldKey: string
  value: string
  placeholder: string
  onChange: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState<string[]>([])
  const [loaded, setLoaded] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const fetchHistory = useCallback(() => {
    getIdentifierHistory(fieldKey).then(vals => {
      setHistory(vals)
      setLoaded(true)
    }).catch(() => setLoaded(true))
  }, [fieldKey])

  const toggle = useCallback(() => {
    if (!open && !loaded) fetchHistory()
    if (!open) fetchHistory()
    setOpen(o => !o)
  }, [open, loaded, fetchHistory])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  const filtered = history.filter(h => h !== value)

  return (
    <div ref={ref} className="relative flex items-center gap-0 w-full max-w-80 min-w-0">
      <Input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 text-sm rounded-r-none border-r-0"
      />
      <button
        type="button"
        onClick={toggle}
        className={`shrink-0 h-9 w-9 flex items-center justify-center rounded-r-md border transition-all ${
          open
            ? 'border-ring bg-accent text-foreground'
            : 'border-input bg-background text-muted-foreground hover:text-foreground hover:bg-accent/50'
        }`}
      >
        <svg
          width="14" height="14" viewBox="0 0 14 14" fill="none"
          className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        >
          <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md py-1 shadow-lg animate-fade-in bg-popover border border-border">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground italic">
              Нет сохранённых идентификаторов
            </div>
          ) : (
            filtered.map(item => (
              <button
                key={item}
                type="button"
                onClick={() => { onChange(item); setOpen(false) }}
                className="w-full text-left px-3 py-1.5 text-sm transition-colors text-foreground hover:bg-accent truncate"
              >
                {item}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function GalaxyIdentifierRow({
  fieldKey, value, placeholder, history, onChange, onRemove, canRemove,
}: {
  fieldKey: string
  value: string
  placeholder: string
  history: string[]
  onChange: (v: string) => void
  onRemove: () => void
  canRemove: boolean
}) {
  // One row of GalaxyIdentifiersField: input + history dropdown arrow.
  // The dropdown surfaces ALL previously-saved identifiers for this field
  // (shared history endpoint, same as IdentifierWithHistory).
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  const filtered = history.filter(h => h !== value)

  return (
    <div className="flex items-center gap-1.5">
      <div ref={ref} className="relative flex items-center gap-0 flex-1 min-w-0">
        <Input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 text-sm rounded-r-none border-r-0"
          data-history-field={fieldKey}
        />
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className={`shrink-0 h-9 w-9 flex items-center justify-center rounded-r-md border transition-all ${
            open
              ? 'border-ring bg-accent text-foreground'
              : 'border-input bg-background text-muted-foreground hover:text-foreground hover:bg-accent/50'
          }`}
          title="История идентификаторов"
        >
          <svg
            width="14" height="14" viewBox="0 0 14 14" fill="none"
            className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          >
            <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        {open && (
          <div className="absolute left-0 top-full z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md py-1 shadow-lg animate-fade-in bg-popover border border-border">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground italic">
                Нет сохранённых идентификаторов
              </div>
            ) : (
              filtered.map(item => (
                <button
                  key={item}
                  type="button"
                  onClick={() => { onChange(item); setOpen(false) }}
                  className="w-full text-left px-3 py-1.5 text-sm transition-colors text-foreground hover:bg-accent truncate"
                >
                  {item}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:text-destructive hover:border-destructive/30 hover:bg-destructive/10 transition-all"
          aria-label="Удалить"
          title="Удалить идентификатор"
        >
          <Minus className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

function GalaxyIdentifiersField({ fieldKey, value, placeholder, onChange }: {
  fieldKey: string
  value: string
  placeholder: string
  onChange: (v: string) => void
}) {
  // Backend stores newline-separated identifiers in a single field.
  // We split into an array for the UI, then re-join on every edit.
  // The "+" button adds a new empty row; minus removes (min 1 row stays).
  // Each row has a history-dropdown arrow that lists every identifier
  // ever saved for this field (shared across all Galaxy projects).
  //
  // Bug #279: also split on a literal '+'. Operators type "a+b+c" into one
  // field (the hint mentions a «+» button), which previously stayed as a
  // single identifier → all clicks stamped with the composite → per-sub-id
  // stats showed zero. Splitting here shows 3 rows and normalises storage to
  // newline-separated. '+' is never a valid identifier char ([A-Za-z0-9_]).
  const items = (() => {
    const parts = String(value ?? '')
      .split(/[\n;,+]/)
      .map(s => s.trim())
    if (parts.length === 0) return ['']
    return parts
  })()

  // Lazy-load history once per session; same fetch the legacy
  // IdentifierWithHistory used.
  const [history, setHistory] = useState<string[]>([])
  const [historyLoaded, setHistoryLoaded] = useState(false)
  useEffect(() => {
    if (historyLoaded) return
    let cancelled = false
    getIdentifierHistory(fieldKey).then(vals => {
      if (!cancelled) {
        setHistory(vals)
        setHistoryLoaded(true)
      }
    }).catch(() => { if (!cancelled) setHistoryLoaded(true) })
    return () => { cancelled = true }
  }, [fieldKey, historyLoaded])

  const commit = (next: string[]) => {
    const cleaned = next.map(s => s.trim()).filter((s, i, arr) => s !== '' || i === arr.length - 1)
    onChange(cleaned.join('\n'))
  }

  const updateAt = (idx: number, v: string) => {
    const next = items.slice()
    next[idx] = v
    commit(next)
  }
  const removeAt = (idx: number) => {
    if (items.length <= 1) return
    const next = items.slice()
    next.splice(idx, 1)
    commit(next.length === 0 ? [''] : next)
  }
  const addNew = () => {
    const next = items.slice()
    next.push('')
    commit(next)
  }

  return (
    <div className="flex flex-col gap-1.5 w-full max-w-80 min-w-0">
      {items.map((item, idx) => (
        <GalaxyIdentifierRow
          key={idx}
          fieldKey={fieldKey}
          value={item}
          placeholder={idx === 0 ? placeholder : `Идентификатор #${idx + 1}`}
          history={history}
          onChange={v => updateAt(idx, v)}
          onRemove={() => removeAt(idx)}
          canRemove={items.length > 1}
        />
      ))}
      <button
        type="button"
        onClick={addNew}
        className="self-start inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary px-2 py-1 rounded-md hover:bg-primary/5 transition-all"
        title="Добавить ещё один идентификатор"
      >
        <Plus className="w-3.5 h-3.5" />
        <span>Добавить идентификатор</span>
      </button>
    </div>
  )
}

function NumberStepper({ def, value, onChange }: {
  def: ResourceDef
  value: any
  onChange: (v: number) => void
}) {
  const min = def.minValue ?? 0
  const max = def.maxValue ?? 9999
  const n = Number(value ?? def.defaultValue) || 0
  const clamp = (v: number) => Math.max(min, Math.min(max, v))

  const holdRef = useRef<number | null>(null)
  const currentRef = useRef<number>(n)
  currentRef.current = n
  const startHold = (delta: number) => {
    // one immediate step
    currentRef.current = clamp(currentRef.current + delta)
    onChange(currentRef.current)
    // repeat while pressed (accelerates after a moment)
    let ticks = 0
    holdRef.current = window.setInterval(() => {
      ticks += 1
      const step = ticks > 8 ? 5 : 1
      currentRef.current = clamp(currentRef.current + delta * step)
      onChange(currentRef.current)
    }, 130)
  }
  const endHold = () => {
    if (holdRef.current) { window.clearInterval(holdRef.current); holdRef.current = null }
  }
  useEffect(() => endHold, [])

  const step = (delta: number) => onChange(clamp(n + delta))
  return (
    <div className="inline-flex items-stretch h-9 rounded-md border border-input bg-background overflow-hidden shadow-xs">
      <button
        type="button"
        onPointerDown={e => { e.preventDefault(); startHold(-1) }}
        onPointerUp={endHold}
        onPointerLeave={endHold}
        onPointerCancel={endHold}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); step(-1) } }}
        disabled={n <= min}
        className="px-3 h-full flex items-center text-muted-foreground hover:text-foreground hover:bg-accent active:bg-accent/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border-r border-input"
        aria-label="Уменьшить"
      >
        <Minus className="w-4 h-4" />
      </button>
      <Input
        type="number"
        min={min}
        max={max}
        value={n}
        onChange={e => {
          let v = Number(e.target.value)
          if (!Number.isFinite(v)) v = min
          onChange(clamp(v))
        }}
        className="w-20 h-full text-center tabular-nums border-0 rounded-none focus-visible:ring-0 shadow-none"
      />
      <button
        type="button"
        onPointerDown={e => { e.preventDefault(); startHold(1) }}
        onPointerUp={endHold}
        onPointerLeave={endHold}
        onPointerCancel={endHold}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); step(1) } }}
        disabled={n >= max}
        className="px-3 h-full flex items-center text-muted-foreground hover:text-foreground hover:bg-accent active:bg-accent/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border-l border-input"
        aria-label="Увеличить"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  )
}

function isBoolResource(def: ResourceDef): boolean {
  return def.type === 'Select'
    && Array.isArray(def.options)
    && def.options.length === 2
    && def.options[0] === '\u0414\u0430'
    && def.options[1] === ''
}

function isToggleSelectResource(def: ResourceDef): boolean {
  return def.type === 'Select' && typeof def.toggleOff === 'string'
}

// Root-level "switch" includes bool ("Да"/"") + Select with toggleOff.
function isSwitchResource(def: ResourceDef): boolean {
  return isBoolResource(def) || isToggleSelectResource(def)
}

// Switch-row in shadcn "Description" style:
//  - mobile: Choice Card — bordered clickable card
//  - desktop: inline row — no border/bg, just text left + switch right, divider between rows
// If there are children (dependent fields shown only when switch is on),
// they render inside the card below the header with a subtle indent.
function BoolFieldRow({ def, value, onChange, hasChildren, children }: {
  def: ResourceDef
  value: any
  onChange: (v: any) => void
  hasChildren?: boolean
  children?: React.ReactNode
}) {
  const isToggleSelect = isToggleSelectResource(def)
  const current = String(value ?? def.defaultValue)
  const checked = isToggleSelect ? current !== def.toggleOff : current === '\u0414\u0430'

  // Auto-scroll: when user turns ON a switch that has dependent fields,
  // scroll the whole card into view so the newly revealed children are visible.
  const rootRef = useRef<HTMLDivElement>(null)
  const prevChecked = useRef(checked)
  useEffect(() => {
    if (!prevChecked.current && checked && hasChildren && rootRef.current) {
      // let layout settle after children render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          // then make sure the bottom-most element is visible too
          setTimeout(() => {
            const el = rootRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const vh = window.innerHeight || document.documentElement.clientHeight
            if (rect.bottom > vh) {
              el.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }
          }, 220)
        })
      })
    }
    prevChecked.current = checked
  }, [checked, hasChildren])

  const toggle = () => {
    if (isToggleSelect) {
      if (checked) {
        onChange(def.toggleOff)
      } else {
        // switch ON — pick first non-off option
        const firstOn = (def.options ?? []).find(o => o !== def.toggleOff) ?? ''
        onChange(firstOn)
      }
    } else {
      onChange(checked ? '' : '\u0414\u0430')
    }
  }
  // When toggle-select is ON, we show an inline dropdown with the non-off options
  // as the first nested item.
  const showInlineSelect = isToggleSelect && checked
  const nonOffOptions = isToggleSelect ? (def.options ?? []).filter(o => o !== def.toggleOff) : []
  const hasKids = checked && (!!hasChildren || showInlineSelect)
  return (
    <div
      ref={rootRef}
      className={`group transition-colors select-none
        rounded-xl border px-4 py-3 ${hasKids ? 'pb-4' : ''}
        md:rounded-xl md:px-4 md:py-3 ${hasKids ? 'md:pb-4' : ''}
        ${!hasKids ? 'md:border-0 md:border-b md:border-border/30 md:last:border-b-0 md:bg-transparent md:rounded-none md:px-2 md:py-3.5' : ''}
        ${hasKids
          ? 'bg-muted/40 border-border md:bg-muted/40'
          : (checked
              ? 'bg-muted/50 border-border md:bg-transparent'
              : 'bg-card border-border md:bg-transparent')}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle() } }}
        className="flex items-center gap-3 cursor-pointer hover:bg-transparent"
      >
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className={`text-sm leading-snug ${checked ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
            {def.description}
          </div>
          {def.hint && (
            <div className="text-[11px] md:text-xs text-muted-foreground leading-snug">
              {def.hint}
            </div>
          )}
        </div>
        <Switch
          checked={checked}
          onCheckedChange={toggle}
          onClick={e => e.stopPropagation()}
          className="shrink-0"
        />
      </div>
      {hasKids && (
        <div className="mt-3 pt-3 border-t border-border/40 space-y-2.5">
          {showInlineSelect && (
            <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:gap-3 py-1.5">
              <label className="text-[12px] md:text-[13px] leading-snug font-medium text-foreground md:w-56 md:shrink-0 flex items-center gap-1.5">
                <span>Режим</span>
              </label>
              <div className="min-w-0 flex-1">
                <CustomDropdown
                  options={nonOffOptions}
                  value={current}
                  onChange={v => onChange(v)}
                />
              </div>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  )
}

function RenderField({ name, def, value, onChange, isAdmin = false }: {
  name: string
  def: ResourceDef
  value: any
  onChange: (v: any) => void
  isAdmin?: boolean
}) {
  switch (def.type) {
    case 'Select':
      // PUSHER TWO is super-admin-only — strip it from the dropdown
      // for regular operators. Old saved configs that *already* have
      // ANONIM_MODE=PUSHER TWO keep working (the value is preserved
      // even if it isn't in the rendered options list).
      if (name === 'ANONIM_MODE' && !isAdmin) {
        const filteredDef: ResourceDef = {
          ...def,
          options: (def.options ?? []).filter(o => o !== 'PUSHER TWO'),
        }
        return <SelectField def={filteredDef} value={value} onChange={onChange} />
      }
      return <SelectField def={def} value={value} onChange={onChange} />


    case 'MultiCheckbox':
      return <MultiCheckboxField def={def} value={value} onChange={onChange} />

    case 'FixedInteger':
      return (
        <Input
          type="number"
          min={def.minValue}
          max={def.maxValue}
          // Bug #64: when current value is the default 0 OR field is
          // empty, render an EMPTY input with placeholder so the
          // operator can type a fresh value without "010" prepending.
          value={value === '' || value === undefined || value === null ? '' : value}
          placeholder={String(def.defaultValue ?? '')}
          onChange={e => {
            const raw = e.target.value
            // Allow empty so user can clear the field freely
            if (raw === '') {
              onChange('')
              return
            }
            let n = Number(raw)
            if (Number.isFinite(n)) {
              if (def.minValue != null && n < def.minValue) n = def.minValue
              if (def.maxValue != null && n > def.maxValue) n = def.maxValue
            }
            onChange(n)
          }}
          className="w-full max-w-28 md:w-28 tabular-nums text-sm min-w-0"
        />
      )

    case 'LinesFromFile':
      return (
        <LinesFromFileField
          name={name}
          value={value}
          description={def.description}
          onChange={onChange}
        />
      )

    case 'FilesFromDirectory':
      return (
        <FilesFromDirectoryField
          value={value}
          description={def.description}
          onChange={onChange}
        />
      )

    case 'FixedString':
    default:
      if (name === 'OTHER_REGION_LR_CUSTOM') {
        return (
          <CustomRegionsField
            value={String(value ?? '')}
            onChange={onChange}
          />
        )
      }
      if (name === 'SEARCH_URL_RANDOM_LIST') {
        return (
          <SearchUrlWeightsField
            value={String(value ?? '')}
            onChange={onChange}
          />
        )
      }
      if (name === 'MODE_CLOUD_PASSWORD_GALAXY') {
        return (
          <GalaxyIdentifiersField
            fieldKey={name}
            value={String(value ?? '')}
            placeholder={def.description}
            onChange={onChange}
          />
        )
      }
      if ((def as any).multiline) {
        return (
          <Textarea
            value={String(value ?? '')}
            onChange={e => onChange(e.target.value)}
            placeholder={def.description}
            rows={3}
            className="w-full max-w-80 text-sm min-w-0 font-mono"
          />
        )
      }
      if (def.withHistory) {
        return (
          <IdentifierWithHistory
            fieldKey={name}
            value={String(value ?? '')}
            placeholder={def.description}
            onChange={onChange}
          />
        )
      }
      return (
        <Input
          type="text"
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          placeholder={def.description}
          className="w-full max-w-80 text-sm min-w-0"
        />
      )
  }
}

function RandomIntegerField({ name, def, cfg, update }: {
  name: string
  def: ResourceDef
  cfg: Settings
  update: (patch: Partial<Settings>) => void
}) {
  const minKey = `${name}_MIN`
  const maxKey = `${name}_MAX`
  // Bug #73 (same root as #64 but for RandomInteger): operator types
  // "22" but field shows "022" because we keep the default 0 instead
  // of letting the field be empty during edit. Render "" when the
  // value is empty/null and use placeholder for the default.
  const rawMin = cfg[minKey]
  const rawMax = cfg[maxKey]
  const minDisp = rawMin === '' || rawMin === undefined || rawMin === null ? '' : rawMin
  const maxDisp = rawMax === '' || rawMax === undefined || rawMax === null ? '' : rawMax
  const minPh = String(def.minValue ?? '')
  const maxPh = String(def.maxValue ?? '')

  const handleChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === '') {
      update({ [key]: '' })
      return
    }
    let n = Number(raw)
    if (Number.isFinite(n)) {
      if (def.minValue != null && n < def.minValue) n = def.minValue
      if (def.maxValue != null && n > def.maxValue) n = def.maxValue
    }
    update({ [key]: n })
  }

  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <Input
        type="number"
        value={minDisp}
        placeholder={minPh}
        onChange={handleChange(minKey)}
        min={def.minValue}
        max={def.maxValue}
        className="w-full max-w-24 md:w-24 tabular-nums text-sm min-w-0"
      />
      <span className="text-xs font-medium text-muted-foreground">{'\u2014'}</span>
      <Input
        type="number"
        value={maxDisp}
        placeholder={maxPh}
        onChange={handleChange(maxKey)}
        min={def.minValue}
        max={def.maxValue}
        className="w-full max-w-24 md:w-24 tabular-nums text-sm min-w-0"
      />
    </div>
  )
}

function SubsectionHeader({ title, className }: { title: string; className?: string }) {
  return (
    <div className={`flex items-center gap-3 mt-6 pt-4 pb-3 first:mt-0 ${className ?? ''}`}>
      <span className="text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap text-muted-foreground">
        {title}
      </span>
      <Separator className="flex-1" />
    </div>
  )
}

function SmartPilotInfo() {
  const [open, setOpen] = useState(false)

  return (
    <div className="mb-6 rounded-xl border border-primary/20 bg-primary/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-primary/10 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2.5">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary shrink-0">
            <path d="M12 2a8 8 0 00-8 8c0 3.4 2.1 6.3 5 7.4V20a1 1 0 001 1h4a1 1 0 001-1v-2.6c2.9-1.1 5-4 5-7.4a8 8 0 00-8-8z"/>
            <line x1="9" y1="24" x2="15" y2="24"/>
          </svg>
          <span className="text-sm font-semibold text-foreground">SmartPilot — как работает автопилот</span>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">AI</Badge>
        </div>
        <svg
          width="14" height="14" viewBox="0 0 12 12" fill="none"
          className={`shrink-0 transition-transform duration-200 text-muted-foreground ${open ? 'rotate-180' : ''}`}
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 text-sm leading-relaxed border-t border-primary/10 pt-4">
          <div>
            <h4 className="font-semibold text-foreground mb-1.5">Ядро принятия решений</h4>
            <p className="text-muted-foreground text-xs">
              SmartPilot — это нейросетевая модель (перцептрон) с 12 входными фичами и самообучающимися весами.
              Для каждого ключевого слова модель анализирует историю позиций, тренд, скорость движения,
              CTR-нагрузку и десятки других факторов, чтобы решить: кликать, пропустить, изменить запрос или скликнуть конкурента.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-lg bg-background/80 border border-border p-3">
              <div className="text-xs font-semibold text-foreground mb-1">Анти-залипание</div>
              <p className="text-[11px] text-muted-foreground">Если сайт залип на ТОП-1 более 3 дней — вероятность клика автоматически снижается, чтобы не вызвать фильтр ботовыдачи.</p>
            </div>
            <div className="rounded-lg bg-background/80 border border-border p-3">
              <div className="text-xs font-semibold text-foreground mb-1">CTR-потолок</div>
              <p className="text-[11px] text-muted-foreground">Модель знает естественный CTR для каждой позиции (ТОП-1: 28%, ТОП-5: 6.5%) и не превышает его более чем в 1.8x.</p>
            </div>
            <div className="rounded-lg bg-background/80 border border-border p-3">
              <div className="text-xs font-semibold text-foreground mb-1">Анализ тренда</div>
              <p className="text-[11px] text-muted-foreground">Сайт растёт — модель ослабляет давление. Падает — усиливает. Новый ключ — умеренная агрессия с постепенным наращиванием.</p>
            </div>
            <div className="rounded-lg bg-background/80 border border-border p-3">
              <div className="text-xs font-semibold text-foreground mb-1">Умные мутации</div>
              <p className="text-[11px] text-muted-foreground">Автоматическая уникализация запросов: синонимы из Яндекс.Саджеста, перестановка слов, управляемые опечатки, морфология.</p>
            </div>
            <div className="rounded-lg bg-background/80 border border-border p-3">
              <div className="text-xs font-semibold text-foreground mb-1">Склик конкурентов</div>
              <p className="text-[11px] text-muted-foreground">В ТОП-2: активный склик для удержания. В ТОП-5+: фокус на своих кликах. При падении — максимум ресурсов на свой сайт.</p>
            </div>
            <div className="rounded-lg bg-background/80 border border-border p-3">
              <div className="text-xs font-semibold text-foreground mb-1">Самообучение</div>
              <p className="text-[11px] text-muted-foreground">Каждые 50 наблюдений модель пересчитывает веса: если позиция улучшилась — стратегия закрепляется, ухудшилась — корректируется.</p>
            </div>
          </div>

          <div className="rounded-lg bg-background/80 border border-border p-3">
            <div className="text-xs font-semibold text-foreground mb-1.5">Типы поведения на SERP</div>
            <div className="grid grid-cols-1 gap-2 text-[11px] text-muted-foreground md:grid-cols-4">
              <div><strong className="text-foreground">Клик</strong> — нашли и кликнули</div>
              <div><strong className="text-foreground">Показ</strong> — нашли, но не кликнули (impression)</div>
              <div><strong className="text-foreground">Уход</strong> — ушли с выдачи без клика</div>
              <div><strong className="text-foreground">Склик</strong> — кликнули конкурента вместо себя</div>
            </div>
          </div>

          <div className="rounded-lg bg-purple-500/10 border border-purple-500/20 p-3">
            <div className="text-xs font-semibold text-foreground mb-1.5">Что контролирует автопилот</div>
            <p className="text-[11px] text-muted-foreground mb-2">
              При активном автопилоте следующие настройки управляются автоматически и скрываются из других разделов:
            </p>
            <div className="grid grid-cols-1 gap-1.5 text-[11px] text-muted-foreground md:grid-cols-2">
              <div>• <strong className="text-foreground">Склик конкурентов</strong> — кол-во, режим, стратегия</div>
              <div>• <strong className="text-foreground">DoubleClick</strong> — шанс и количество повторных кликов</div>
              <div>• <strong className="text-foreground">Время на сайте</strong> — адаптивный модификатор</div>
              <div>• <strong className="text-foreground">Уникализация запросов</strong> — мутации вместо UNICKEY/Реверса</div>
              <div>• <strong className="text-foreground">Генерация хвостов (LSI)</strong> — заменяется умными мутациями</div>
              <div>• <strong className="text-foreground">Вероятность клика</strong> — основное решение нейросети</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ApiDocumentation() {
  const [open, setOpen] = useState(false)

  return (
    <div className="mb-6 rounded-xl border border-border bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-accent/50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2.5">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary shrink-0">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10,9 9,9 8,9"/>
          </svg>
          <span className="text-sm font-semibold text-foreground">Документация API для разработчиков</span>
        </div>
        <svg
          width="14" height="14" viewBox="0 0 12 12" fill="none"
          className={`shrink-0 transition-transform duration-200 text-muted-foreground ${open ? 'rotate-180' : ''}`}
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5 text-sm leading-relaxed border-t border-border pt-4">

          <div>
            <h4 className="font-semibold text-foreground mb-1.5">Как это работает</h4>
            <p className="text-muted-foreground">
              Бустер по таймеру запрашивает ваш API за заданием (GET-запрос), выполняет его, 
              и отправляет результат обратно (POST-запрос). Вы поднимаете свой сервер, 
              который раздаёт задания и принимает результаты.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">1. Получение задания — GET-запрос</h4>
            <p className="text-muted-foreground mb-2">
              Бустер отправляет <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">GET</code> на вашу ссылку. 
              Ваш сервер должен вернуть JSON:
            </p>
            <div className="rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20 px-3 py-2 mb-2">
              <p className="text-xs text-amber-800 dark:text-amber-400 font-medium">Ниже — пример формата. Все значения условные, не копируйте их. Указывайте свои реальные данные.</p>
            </div>
            <pre className="text-xs bg-muted/70 rounded-lg p-3.5 overflow-x-auto font-mono text-foreground leading-relaxed">{`{
  "site": "example.com",          // * домен (example.com)
  "key": "купить диван москва",   // * поисковый запрос

  // ─── Задание ───
  "task_id": "abc-123",           // ID задания (вернётся в результате)
  "region": "213",                // Яндекс LR (по умолч. 213)
  "brand": "IKEA",                // одиночный бренд (или мульти, см. ниже)
  // Мульти-бренд: список через ; или : (равная вероятность) — "IKEA;ИКЕЯ;Икеа"
  // Или с весами через | : "90;IKEA|10;ИКЕЯ" = 90 проц IKEA, 10 проц ИКЕЯ
  "brand_mode": "if_not_found",   // none | if_not_found | always | precheck
  "target_url": "example.com/catalog/divany",// точный URL для матчинга в SERP

  // ─── Настройки поиска ───
  "serp_pages": 3,                // макс. страниц выдачи
  "max_position": 30,             // кликать если позиция ≤ N
  "visit_site": true,             // заходить на сайт
  "speedy": true,                 // быстрое поведение на выдаче
  "search_url": "https://yandex.ru",  // URL поиска (по умолч. yandex.ru)

  // ─── Настройки сайта ───
  "walk_time": 45,                // сек. на сайте
  "click_depth": 3,               // страниц внутри сайта
  "block_metrika": true,          // блокировать Метрику
  "sclick": {                     // склик конкурентов
    "enabled": true, "count": 1,
    "mode": "random"              // random | directed | protected
  },
  "lsi_words": ["недорого", ...], // LSI-слова к запросу
  "unickey": 50,                  // уникализатор (шанс %)
  "revers_word": 30,              // реверсивный ввод (шанс %)
  "url_tail": true,               // генерация хвоста URL
  "multisession": 3,              // мульти-сессия (кол-во)
  "traffic_limit": 50,            // лимит трафика МБ
  "double_click": {               // повторный клик
    "count": 2, "chance": 80
  },
  "region_dilute": {              // разбавление LR
    "enabled": true, "chance": 50,
    "mode": "sub"                 // sub | all | mix
  },

  // ─── Профили / Galaxy ───
  "profile_mode": "GALAXY PRO",  // GALAXY PRO | SPACE PRO | PUSHER TWO | GHOST
  "profile_id": "myaccount1",    // идентификатор профилей
  "adapt_profiles": true,        // адаптировать для CreepBrowser
  "profile_filter": {            // фильтрация (Galaxy/Space)
    "enabled": true,
    "mode": "unique"             // unique | used
  },
  "profile_group": {             // группировка (Galaxy)
    "enabled": true, "name": "group1"
  },
  "repeat_click": {              // повторный клик (Galaxy)
    "enabled": true,
    "chance": 90, "cycle": 1000
  },
  "pusher_source": "src1",       // источник (PUSHER TWO)
  "pusher_group": "grp1",        // группа (PUSHER TWO)

  // ─── Прокси: ОДИН из двух способов ───
  "proxy_url": "https://...",      // вариант А: ссылка-провайдер, отдаёт IP:PORT:USER:PASS на каждый GET
  "proxy_ipport": "1.2.3.4:8080:user:pass",  // вариант Б: конкретный прокси (Bug #81)
  "proxy_format": "SOCKS5",        // HTTP (по умолч.) или SOCKS5

  // ─── Мета ───
  "callback_url": "https://...",  // URL для результата
  "priority": "high",
  "meta": { "client_id": 42 }
}`}</pre>
            <div className="mt-3 space-y-2 text-xs text-muted-foreground">
              <p>
                <strong>Обязательные:</strong> <code className="bg-muted px-1 py-0.5 rounded font-mono">site</code> и <code className="bg-muted px-1 py-0.5 rounded font-mono">key</code>. 
                Остальные — опционально. Если задач нет — верните пустой ответ или <code className="bg-muted px-1 py-0.5 rounded font-mono">"No tasks"</code>.
              </p>
              <div className="rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20 px-3 py-2">
                <p className="font-semibold text-amber-800 dark:text-amber-400 mb-1">Формат полей — как включить и отключить:</p>
                <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                  <li><code className="bg-muted px-1 rounded font-mono">site</code> — реальный домен (<code>myshop.ru</code>), НЕ идентификатор проекта</li>
                  <li><code className="bg-muted px-1 rounded font-mono">brand</code> — слово/фраза. Поддерживается мульти-бренд: список через <code>;</code>/<code>:</code> (равная вероятность, напр. <code>"IKEA;ИКЕЯ"</code>) или с весами через <code>|</code> (<code>"90;IKEA|10;ИКЕЯ"</code> = 90% IKEA, 10% ИКЕЯ). <code className="bg-muted px-1 rounded font-mono">brand_mode</code>: <code>none</code> (выкл), <code>if_not_found</code>, <code>always</code>, <code>precheck</code> (AJAX-предпроверка)</li>
                  <li><code className="bg-muted px-1 rounded font-mono">target_url</code> — точный URL для матчинга в SERP (<code>example.com/catalog</code> или <code>https://example.com/catalog</code>). Протокол и www. убираются автоматически, регистр не важен, слэш в конце не обязателен</li>
                  <li><code className="bg-muted px-1 rounded font-mono">proxy_url</code> — ссылка на ваше API прокси. Каждый GET должен возвращать одну строку: <code>IP:PORT:USER:PASS</code>. Режим «По ссылке».</li>
                  <li><code className="bg-muted px-1 rounded font-mono">proxy_ipport</code> — конкретный прокси одной строкой <code>IP:PORT[:USER:PASS]</code>. Режим «Конкретное» (приоритет над <code>proxy_url</code>).</li>
                  <li><code className="bg-muted px-1 rounded font-mono">proxy_format</code>: <code>HTTP</code> (по умолч.) или <code>SOCKS5</code></li>
                </ul>
              </div>
              <div className="rounded-md bg-sky-500/10 border border-sky-500/20 px-3 py-2 mt-2">
                <p className="font-semibold text-sky-600 dark:text-sky-400 mb-1">Как отключить функцию через API:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-1 pr-3 font-medium">Поле</th>
                        <th className="py-1 pr-3 font-medium">Включить</th>
                        <th className="py-1 font-medium">Отключить</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono text-[11px]">
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">visit_site</td><td className="py-1 pr-3">true</td><td className="py-1">false</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">speedy</td><td className="py-1 pr-3">true</td><td className="py-1">false</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">block_metrika</td><td className="py-1 pr-3">true</td><td className="py-1">false</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">url_tail</td><td className="py-1 pr-3">true</td><td className="py-1">false</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">adapt_profiles</td><td className="py-1 pr-3">true</td><td className="py-1">false</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">unickey</td><td className="py-1 pr-3">true или 50 (шанс%)</td><td className="py-1">false или 0</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">revers_word</td><td className="py-1 pr-3">true или 30 (шанс%)</td><td className="py-1">false или 0</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">multisession</td><td className="py-1 pr-3">3 (кол-во)</td><td className="py-1">0</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">traffic_limit</td><td className="py-1 pr-3">50 (МБ)</td><td className="py-1">0</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">{`double_click`}</td><td className="py-1 pr-3">{`{"count":2,"chance":80}`} или 2</td><td className="py-1">0</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">{`sclick`}</td><td className="py-1 pr-3">{`{"enabled":true, ...}`}</td><td className="py-1">{`false`} или {`{"enabled":false}`}</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">{`region_dilute`}</td><td className="py-1 pr-3">{`{"enabled":true,"chance":50,"mode":"sub"}`}</td><td className="py-1">false</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">profile_filter</td><td className="py-1 pr-3">{`{"enabled":true,"mode":"unique"}`} или true</td><td className="py-1">false</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">profile_group</td><td className="py-1 pr-3">{`{"enabled":true,"name":"grp"}`}</td><td className="py-1">false</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">repeat_click</td><td className="py-1 pr-3">{`{"enabled":true,"chance":90,"cycle":1000}`}</td><td className="py-1">false</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">brand_mode</td><td className="py-1 pr-3">"if_not_found", "always" или "precheck"</td><td className="py-1">"none"</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3">proxy_url</td><td className="py-1 pr-3">"https://my-proxy-api/get"</td><td className="py-1">не указывать поле</td></tr>
                      <tr><td className="py-1 pr-3">proxy_ipport</td><td className="py-1 pr-3">"1.2.3.4:8080:user:pass"</td><td className="py-1">не указывать поле</td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-1.5 text-muted-foreground">Поля, не указанные в ответе, берутся из настроек кампании в панели.</p>
                <p className="mt-1.5 text-muted-foreground"><strong>Совет:</strong> чтобы сопоставить прокси с результатом — присылайте <code className="bg-muted px-1 py-0.5 rounded font-mono">proxy_ipport</code> и тот же IP вернётся в callback. С <code className="bg-muted px-1 py-0.5 rounded font-mono">proxy_url</code> панель не знает какой именно IP отдала ваша провайдерская ссылка.</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">2. Результат — POST-запрос</h4>
            <p className="text-muted-foreground mb-2">
              После выполнения бустер отправляет <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">POST</code> JSON 
              на ссылку обратной связи (или <code className="bg-muted px-1 py-0.5 rounded font-mono">callback_url</code> из задания):
            </p>
            <pre className="text-xs bg-muted/70 rounded-lg p-3.5 overflow-x-auto font-mono text-foreground leading-relaxed">{`{
  "task_id": "abc-123",           // эхо вашего task_id
  "site": "example.com",
  "key": "купить диван москва",
  "position": 5,                  // позиция в выдаче (0 = не найден)
  "page_found": 1,                // на какой странице SERP нашли
  "success": 1,                   // 1 = клик выполнен, 0 = ошибка
  "elapsed": 42,                  // общее время (сек.)
  "time_on_site": 28,             // сек. на целевом сайте
  "internal_pages": 3,            // страниц посещено на сайте
  "found_url": "https://...",     // реальный URL клика
  "pages_checked": 2,             // страниц SERP просканировано
  "brand_used": "IKEA",           // какой бренд использовался
  "brand_found": true,            // найден через бренд (position ≈ 1)
  "captcha_hit": false,           // была ли капча
  "captcha_solved": false,        // решена ли
  "competitor_clicked": "rival.ru",// склик — домен конкурента
  "double_clicked": 0,            // повторных кликов выполнено
  "serp_urls": ["a.com", ...],   // домены в выдаче (конкурентный анализ)
  "error": "",                    // текст ошибки (если success=0)
  "priority": "high",             // эхо приоритета
  "meta": { "client_id": 42 },   // эхо ваших данных
  "timestamp": 1709683200
}`}</pre>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">3. Примеры</h4>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Минимальный ответ (только обязательные поля):</p>
                <pre className="text-xs bg-muted/70 rounded-lg p-3 overflow-x-auto font-mono text-foreground">{`{"site": "example.com", "key": "купить диван"}`}</pre>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">С контролем позиции и брендом:</p>
                <pre className="text-xs bg-muted/70 rounded-lg p-3 overflow-x-auto font-mono text-foreground">{`{
  "site": "myshop.ru",
  "key": "холодильник samsung",
  "region": "2",
  "brand": "Samsung",
  "max_position": 10,
  "task_id": "order-7891"
}`}</pre>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Полный контроль + склик конкурента:</p>
                <pre className="text-xs bg-muted/70 rounded-lg p-3 overflow-x-auto font-mono text-foreground">{`{
  "site": "myshop.ru",
  "key": "купить ноутбук",
  "region": "213",
  "task_id": "t-456",
  "walk_time": 60,
  "click_depth": 4,
  "block_metrika": true,
  "sclick": {"enabled": true, "count": 1, "mode": "random"},
  "callback_url": "https://api.myshop.ru/cb",
  "meta": {"campaign": "winter_sale"}
}`}</pre>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">С точным URL для матчинга в выдаче:</p>
                <pre className="text-xs bg-muted/70 rounded-lg p-3 overflow-x-auto font-mono text-foreground">{`{
  "site": "myshop.ru",
  "key": "купить ноутбук",
  "task_id": "exact-789",
  "target_url": "myshop.ru/catalog/notebooks",
  "walk_time": 45,
  "click_depth": 3
}`}</pre>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-primary/5 border border-primary/10 px-4 py-3">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Алиасы полей:</strong> вместо <code className="bg-muted px-1 rounded font-mono">site</code> можно <code className="bg-muted px-1 rounded font-mono">domain</code>, 
              вместо <code className="bg-muted px-1 rounded font-mono">key</code> — <code className="bg-muted px-1 rounded font-mono">query</code> или <code className="bg-muted px-1 rounded font-mono">keyword</code>, 
              вместо <code className="bg-muted px-1 rounded font-mono">region</code> — <code className="bg-muted px-1 rounded font-mono">lr</code>.
              Заголовок: <code className="bg-muted px-1 rounded font-mono">Content-Type: application/json</code>.
              User-Agent: <code className="bg-muted px-1 rounded font-mono">CreepBooster/2.0</code>.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              const md = `# CreepBooster API — Документация

## Как это работает

Бустер по таймеру запрашивает ваш API за заданием (GET-запрос), выполняет его,
и отправляет результат обратно (POST-запрос). Вы поднимаете свой сервер,
который раздаёт задания и принимает результаты.

---

## 1. Получение задания — GET-запрос

Бустер отправляет \`GET\` на вашу ссылку. Ваш сервер должен вернуть JSON:

> **Все значения ниже — примерные.** Не копируйте их как есть. Укажите свои реальные данные (домен, ключи, регион и т.д.).

\`\`\`json
{
  "site": "example.com",
  "key": "купить диван москва",

  "task_id": "abc-123",
  "region": "213",
  "brand": "IKEA",
  "brand_mode": "if_not_found",
  "target_url": "example.com/catalog/divany",

  "serp_pages": 3,
  "max_position": 30,
  "visit_site": true,
  "speedy": true,
  "search_url": "https://yandex.ru",

  "walk_time": 45,
  "click_depth": 3,
  "block_metrika": true,

  "sclick": { "enabled": true, "count": 1, "mode": "random" },
  "lsi_words": ["недорого", "с доставкой"],

  "unickey": 50,
  "revers_word": 30,
  "url_tail": true,
  "multisession": 3,
  "traffic_limit": 50,
  "double_click": { "count": 2, "chance": 80 },
  "region_dilute": { "enabled": true, "chance": 50, "mode": "sub" },

  "profile_mode": "GALAXY PRO",
  "profile_id": "myaccount1",
  "adapt_profiles": true,
  "profile_filter": { "enabled": true, "mode": "unique" },
  "profile_group": { "enabled": true, "name": "group1" },
  "repeat_click": { "enabled": true, "chance": 90, "cycle": 1000 },
  "pusher_source": "src1",
  "pusher_group": "grp1",

  "proxy_url": "https://my-proxy-api.com/get",
  "proxy_format": "SOCKS5",

  "callback_url": "https://...",
  "priority": "high",
  "meta": { "client_id": 42 }
}
\`\`\`

**Обязательные:** \`site\` и \`key\`. Остальные — опционально.
Если задач нет — верните пустой ответ или \`"No tasks"\`.

### Описание полей

| Поле | Тип | Описание |
|------|-----|----------|
| \`site\` | string | **Обязательное.** Домен сайта (example.com) |
| \`key\` | string | **Обязательное.** Поисковый запрос |
| \`task_id\` | string | ID задания (вернётся в результате) |
| \`region\` | string | Яндекс LR (по умолч. "213" — Москва) |
| \`brand\` | string | Брендовое слово/фраза |
| \`brand_mode\` | string | Режим бренда: \`"none"\` / \`"if_not_found"\` / \`"always"\` / \`"precheck"\` (AJAX-предпроверка) |
| \`target_url\` | string | Точный URL для матчинга в SERP (\`example.com/catalog/phones\`). Протокол/www убираются автоматически |
| \`serp_pages\` | int | Макс. страниц выдачи для поиска |
| \`max_position\` | int | Кликать только если позиция ≤ N |
| \`visit_site\` | bool | Заходить на сайт (false = только найти) |
| \`speedy\` | bool | Быстрое поведение на выдаче |
| \`search_url\` | string | URL поиска (по умолч. yandex.ru) |
| \`walk_time\` | int | Секунд на сайте |
| \`click_depth\` | int | Внутренних страниц посетить |
| \`block_metrika\` | bool | Блокировать Яндекс.Метрику |
| \`sclick\` | object | Склик: \`{ enabled, count, mode }\`. mode: random/directed/protected |
| \`lsi_words\` | array | LSI-слова к запросу |
| \`unickey\` | bool/int | Уникализатор фраз (true=100%, число=шанс%) |
| \`revers_word\` | bool/int | Реверсивный ввод (true=100%, число=шанс%) |
| \`url_tail\` | bool | Генерация хвоста URL (?word=random) |
| \`multisession\` | int | Мульти-сессия (кол-во, 0=выкл) |
| \`traffic_limit\` | int | Лимит трафика МБ (0=без лимита) |
| \`double_click\` | int/object | Повторный клик: число или \`{ count, chance }\` |
| \`region_dilute\` | bool/int/object | Разбавление LR: \`{ enabled, chance, mode }\`. mode: sub/all/mix |
| \`profile_mode\` | string | Метод работы: GALAXY PRO, SPACE PRO, PUSHER TWO, GHOST |
| \`profile_id\` | string | Идентификатор аккаунта профилей |
| \`adapt_profiles\` | bool | Адаптировать профили для CreepBrowser |
| \`profile_filter\` | bool/object | Фильтрация профилей: \`{ enabled, mode }\`. mode: unique/used |
| \`profile_group\` | bool/object | Группировка Galaxy: \`{ enabled, name }\` |
| \`repeat_click\` | bool/object | Повторный клик Galaxy: \`{ enabled, chance, cycle }\` |
| \`pusher_source\` | string | Источник (PUSHER TWO) |
| \`pusher_group\` | string | Группа (PUSHER TWO) |
| \`proxy_url\` | string | Ссылка на API прокси (режим «По ссылке»). GET → IP:PORT:USER:PASS |
| \`proxy_format\` | string | Формат прокси: HTTP (по умолч.) или SOCKS5 |
| \`callback_url\` | string | URL для результата (override) |
| \`priority\` | string | Приоритет (для логов) |
| \`meta\` | object | Любые данные — вернутся в результате |

### Как включить / отключить функцию

| Поле | Включить | Отключить |
|------|----------|-----------|
| visit_site | \`true\` | \`false\` |
| speedy | \`true\` | \`false\` |
| block_metrika | \`true\` | \`false\` |
| url_tail | \`true\` | \`false\` |
| adapt_profiles | \`true\` | \`false\` |
| unickey | \`true\` или \`50\` (шанс%) | \`false\` или \`0\` |
| revers_word | \`true\` или \`30\` (шанс%) | \`false\` или \`0\` |
| multisession | \`3\` (кол-во) | \`0\` |
| traffic_limit | \`50\` (МБ) | \`0\` |
| double_click | \`{"count":2,"chance":80}\` или \`2\` | \`0\` |
| sclick | \`{"enabled":true, ...}\` | \`false\` или \`{"enabled":false}\` |
| region_dilute | \`{"enabled":true,"chance":50,"mode":"sub"}\` | \`false\` |
| profile_filter | \`{"enabled":true,"mode":"unique"}\` или \`true\` | \`false\` |
| profile_group | \`{"enabled":true,"name":"grp"}\` | \`false\` |
| repeat_click | \`{"enabled":true,"chance":90,"cycle":1000}\` | \`false\` |
| brand_mode | \`"if_not_found"\`, \`"always"\` или \`"precheck"\` | \`"none"\` |
| proxy_url | \`"https://my-proxy-api/get"\` | не указывать поле |

> Поля, не указанные в ответе, берутся из настроек кампании в панели.

---

## 2. Результат — POST-запрос

После выполнения бустер отправляет \`POST\` JSON на ссылку обратной связи
(или \`callback_url\` из задания):

\`\`\`json
{
  "task_id": "abc-123",
  "site": "example.com",
  "key": "купить диван москва",
  "position": 5,
  "page_found": 1,
  "success": 1,
  "elapsed": 42,
  "time_on_site": 28,
  "internal_pages": 3,
  "found_url": "https://example.com/catalog",
  "pages_checked": 2,
  "brand_used": "IKEA",
  "brand_found": true,
  "captcha_hit": false,
  "captcha_solved": false,
  "competitor_clicked": "rival.ru",
  "double_clicked": 0,
  "serp_urls": ["a.com", "b.com"],
  "error": "",
  "priority": "high",
  "meta": { "client_id": 42 },
  "timestamp": 1709683200
}
\`\`\`

| Поле | Описание |
|------|----------|
| \`position\` | Позиция в выдаче (0 = не найден) |
| \`page_found\` | На какой странице SERP нашли |
| \`success\` | 1 = клик выполнен, 0 = ошибка |
| \`brand_found\` | Найден через бренд (position при этом ≈ 1) |
| \`captcha_hit\` / \`captcha_solved\` | Была ли капча / решена ли |
| \`serp_urls\` | Домены в выдаче (конкурентный анализ) |

---

## 3. Примеры

**Минимальный:**
\`\`\`json
{"site": "example.com", "key": "купить диван"}
\`\`\`

**С брендом и позицией:**
\`\`\`json
{
  "site": "myshop.ru",
  "key": "холодильник samsung",
  "region": "2",
  "brand": "Samsung",
  "max_position": 10,
  "task_id": "order-7891"
}
\`\`\`

**Полный контроль + склик:**
\`\`\`json
{
  "site": "myshop.ru",
  "key": "купить ноутбук",
  "region": "213",
  "task_id": "t-456",
  "walk_time": 60,
  "click_depth": 4,
  "block_metrika": true,
  "sclick": {"enabled": true, "count": 1, "mode": "random"},
  "callback_url": "https://api.myshop.ru/cb",
  "meta": {"campaign": "winter_sale"}
}
\`\`\`

---

**Алиасы:** site → domain, key → query/keyword, region → lr
**Заголовок:** Content-Type: application/json
**User-Agent:** CreepBooster/2.0
`
              const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' })
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = 'CreepBooster_API_Documentation.md'
              a.click()
              URL.revokeObjectURL(url)
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-muted/40 hover:bg-muted transition-colors text-sm font-medium text-foreground cursor-pointer"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Скачать документацию (.md)
          </button>
        </div>
      )}
    </div>
  )
}

interface ApiTestResult {
  ok: boolean
  error?: string
  message?: string
  status_code: number
  raw: string
  empty?: boolean
  parsed?: Record<string, unknown>
  fields?: { field: string; value: unknown; status: string; hint?: string }[]
  missing_required?: string[]
  not_sent?: string[]
}

const API_FIELD_HINTS: Record<string, string> = {
  site: 'Домен сайта для продвижения (например myshop.ru). Обязательное поле.',
  key: 'Поисковый запрос, по которому будет происходить поиск. Обязательное поле.',
  task_id: 'Уникальный ID задания. Вернётся в POST-результате, чтобы вы могли сопоставить.',
  region: 'Код региона Яндекса (LR). 213 = Москва, 2 = Санкт-Петербург и т.д.',
  brand: 'Брендовое слово или фраза, которая добавляется к запросу (через ";", "," или ":" если несколько).',
  brand_mode: 'Режим бренда: none — выкл., if_not_found — добавить если сайт не найден, always — всегда, precheck — AJAX-предпроверка.',
  target_url: 'Точный URL страницы для матчинга в SERP (domain/path). Протокол и www убираются автоматически.',
  serp_pages: 'Макс. кол-во страниц выдачи для поиска сайта (0 = только первая).',
  max_position: 'Кликать по сайту только если он на позиции не ниже этого числа.',
  visit_site: 'true — заходить на сайт после клика, false — только найти и кликнуть.',
  speedy: 'true — быстрое поведение на выдаче (меньше задержек).',
  search_url: 'Базовый URL поисковой системы. По умолчанию https://yandex.ru.',
  walk_time: 'Время (сек.) проведённое на сайте после клика.',
  click_depth: 'Кол-во внутренних переходов по ссылкам на сайте.',
  block_metrika: 'true — заблокировать Яндекс.Метрику на целевом сайте.',
  sclick: 'Склик конкурентов. Объект: enabled, count, mode (random/directed/protected).',
  lsi_words: 'LSI-хвосты — массив слов, одно из которых случайно добавится к запросу.',
  unickey: 'Шанс (%) применения уникализации ввода ключевого слова.',
  revers_word: 'Шанс (%) реверсивного ввода (сначала конец, потом начало запроса).',
  url_tail: 'true — генерировать случайный хвост URL при переходе на сайт.',
  multisession: 'Кол-во мульти-сессий (повторных поисков + кликов за один профиль).',
  traffic_limit: 'Лимит трафика в МБ за сессию.',
  double_click: 'Повторный клик по сайту. Объект: count — сколько раз, chance — шанс %.',
  region_dilute: 'Разбавление LR региона. enabled, chance (%), mode (sub/all/mix).',
  profile_mode: 'Метод работы с профилями: GALAXY PRO, SPACE PRO, PUSHER TWO, GHOST.',
  profile_id: 'Идентификатор (аккаунт / таблица) для подключения к профилям.',
  adapt_profiles: 'true — адаптировать Galaxy/Space профили для CreepBrowser.',
  profile_filter: 'Фильтрация профилей. true или объект {enabled, mode: "unique"/"used"}.',
  profile_group: 'Группировка профилей по папке. {enabled, name: "имя_группы"}.',
  repeat_click: 'Разрешить повторный клик для уже кликнутых профилей. {enabled, chance, cycle}.',
  pusher_source: 'Источник для PUSHER TWO (например "bd1").',
  pusher_group: 'Группа для PUSHER TWO.',
  proxy_url: 'Ссылка на ваше API прокси. GET должен вернуть одну строку: IP:PORT:USER:PASS.',
  proxy_ipport: 'Конкретный прокси одной строкой "IP:PORT" или "IP:PORT:USER:PASS". В отличие от proxy_url, этот же IP вернётся в callback — удобно для аналитики «прокси → результат».',
  proxy_format: 'Формат прокси: HTTP (по умолч.) или SOCKS5.',
}

function FieldTooltip({ hint }: { hint: string }) {
  const [show, setShow] = useState(false)
  return (
    <span className="relative inline-flex ml-1">
      <span
        className="cursor-help inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-muted-foreground/15 text-muted-foreground text-[9px] font-bold leading-none hover:bg-primary/20 hover:text-primary transition-colors select-none"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        i
      </span>
      {show && (
        <span className="absolute z-50 left-5 top-1/2 -translate-y-1/2 w-56 px-2.5 py-1.5 rounded-md bg-popover border border-border shadow-lg text-[11px] text-popover-foreground leading-snug pointer-events-none whitespace-normal">
          {hint}
        </span>
      )}
    </span>
  )
}

const API_LOCAL_ITEMS = [
  { key: 'API_LOCAL_PROXY', label: 'Прокси и анонимность', hint: 'Прокси берутся из вкладки «Прокси», а не из поля proxy_url API' },
  { key: 'API_LOCAL_SEARCH', label: 'Настройки поиска', hint: 'serp_pages, visit_site, speedy, search_url — из локальных настроек' },
  { key: 'API_LOCAL_SITE', label: 'Поведение на сайте', hint: 'walk_time, click_depth, metrika, sclick, double_click — локальные' },
  { key: 'API_LOCAL_PROFILES', label: 'Профили и аккаунты', hint: 'profile_mode, profile_id, группировка — из локальных настроек' },
  { key: 'API_LOCAL_SMART', label: 'Умный режим (SmartPilot)', hint: 'Настройки SmartPilot — из локальных настроек' },
] as const

function ApiLocalSection({ cfg, onChange }: { cfg: Record<string, unknown>; onChange: (key: string, val: string) => void }) {
  const activeCount = API_LOCAL_ITEMS.filter(i => cfg[i.key] === 'Да').length
  const [expanded, setExpanded] = useState(activeCount > 0)

  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-accent/30 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
          <span className="text-sm font-semibold text-foreground">Локальные переопределения</span>
        </div>
        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeCount} из {API_LOCAL_ITEMS.length}
            </Badge>
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className={`text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          ><polyline points="6 9 12 15 18 9"/></svg>
        </div>
      </button>
      {expanded && (
        <>
          <div className="px-4 pb-3 border-b border-border">
            <p className="text-xs text-muted-foreground leading-relaxed">
              По умолчанию все настройки берутся из API-задания. Включите модуль, чтобы его настройки использовались из локальных вкладок вместо API.
            </p>
          </div>
          <div className="divide-y divide-border">
            {API_LOCAL_ITEMS.map(item => {
              const active = cfg[item.key] === 'Да'
              return (
                <div
                  key={item.key}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground">{item.label}</span>
                    <p className="text-xs text-muted-foreground leading-tight mt-0.5">{item.hint}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-medium transition-colors ${active ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                      {active ? 'Локально' : 'API'}
                    </span>
                    <Switch
                      checked={active}
                      onCheckedChange={(checked) => onChange(item.key, checked ? 'Да' : '')}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function ApiTestButton({ url }: { url: string }) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ApiTestResult | null>(null)

  const runTest = async () => {
    setLoading(true)
    setResult(null)
    setOpen(true)
    try {
      const r = await fetch('/api/test-api-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      })
      const data = await r.json()
      setResult(data)
    } catch (e: unknown) {
      setResult({ ok: false, error: `Ошибка запроса: ${e}`, status_code: 0, raw: '' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={runTest}
        disabled={loading}
        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-muted/50 hover:bg-muted transition-colors text-foreground disabled:opacity-50"
      >
        {loading ? (
          <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
            <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>
          </svg>
        )}
        {loading ? 'Запрос...' : 'Тестовый запрос'}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div
            className="bg-background border border-border rounded-xl shadow-2xl w-[calc(100vw-2rem)] max-w-[700px] max-h-[80vh] flex flex-col overflow-hidden animate-fade-in mx-4 md:mx-0"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                Тест API: проверка ответа
              </h3>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
              {loading && (
                <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" />
                  </svg>
                  Отправка GET-запроса...
                </div>
              )}

              {result && !loading && (
                <>
                  <div className={`flex items-center gap-2 px-3.5 py-2.5 rounded-lg border text-sm font-medium ${
                    result.ok
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                      : 'bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400'
                  }`}>
                    {result.ok ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22,4 12,14.01 9,11.01"/></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                    )}
                    {result.error || result.message || 'OK'}
                  </div>

                  {result.status_code > 0 && (
                    <div className="text-xs text-muted-foreground">
                      HTTP статус: <span className={`font-mono font-semibold ${result.status_code === 200 ? 'text-emerald-500' : 'text-red-500'}`}>{result.status_code}</span>
                    </div>
                  )}

                  {result.empty && (
                    <div className="px-3.5 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20 text-amber-800 dark:text-amber-400 text-xs">
                      API ответил пустым ответом. Если задачи есть — проверьте формат и URL.
                    </div>
                  )}

                  {result.missing_required && result.missing_required.length > 0 && (
                    <div className="px-3.5 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-xs font-semibold text-red-500 mb-1">Отсутствуют обязательные поля:</p>
                      <div className="flex gap-1.5 flex-wrap">
                        {result.missing_required.map(f => (
                          <span key={f} className="px-2 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400 font-mono text-[11px] font-medium">{f}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.fields && result.fields.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Полученные поля ({result.fields.length}):</p>
                      <div className="rounded-lg border border-border overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-muted/50 border-b border-border">
                              <th className="text-left py-1.5 px-3 font-medium w-8"></th>
                              <th className="text-left py-1.5 px-3 font-medium">Поле</th>
                              <th className="text-left py-1.5 px-3 font-medium">Значение</th>
                              <th className="text-left py-1.5 px-3 font-medium">Статус</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.fields.map((f, i) => (
                              <tr key={i} className="border-b border-border/50 last:border-0">
                                <td className="py-1.5 px-3">
                                  {f.status === 'ok' ? (
                                    <span className="text-emerald-500">&#10003;</span>
                                  ) : (
                                    <span className="text-amber-500">?</span>
                                  )}
                                </td>
                                <td className="py-1.5 px-3 font-mono font-medium text-foreground">
                                  {f.field}
                                  {API_FIELD_HINTS[f.field] && <FieldTooltip hint={API_FIELD_HINTS[f.field]} />}
                                </td>
                                <td className="py-1.5 px-3 text-muted-foreground font-mono break-all">
                                  {typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value)}
                                </td>
                                <td className="py-1.5 px-3">
                                  {f.status === 'ok' ? (
                                    <span className="text-emerald-500 font-medium">OK</span>
                                  ) : (
                                    <span className="text-amber-500">{f.hint}</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {result.not_sent && result.not_sent.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-500"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        Не переданные параметры ({result.not_sent.length}):
                      </p>
                      <p className="text-[11px] text-muted-foreground mb-2">
                        Эти параметры поддерживаются, но не были отправлены API. Будут использованы значения по умолчанию из настроек копии.
                      </p>
                      <div className="flex gap-1.5 flex-wrap">
                        {result.not_sent.map(f => (
                          <span key={f} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted border border-border text-muted-foreground font-mono text-[11px]">
                            {f}
                            {API_FIELD_HINTS[f] && <FieldTooltip hint={API_FIELD_HINTS[f]} />}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.raw && (
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Сырой ответ:</p>
                      <pre className="text-[11px] bg-muted/70 rounded-lg p-3 overflow-x-auto font-mono text-muted-foreground max-h-48 overflow-y-auto whitespace-pre-wrap break-all">{result.raw}</pre>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
              <button
                type="button"
                onClick={runTest}
                disabled={loading}
                className="px-3.5 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                Повторить запрос
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-3.5 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-muted transition-colors"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function ConfigModeToggle({ isApi, onChange }: { isApi: boolean; onChange: (api: boolean) => void }) {
  return (
    <div className="mb-6 inline-flex w-full max-w-full gap-1 rounded-xl bg-muted/60 p-1 md:p-1.5 md:w-auto">
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-3 md:px-4 py-2 text-sm font-medium rounded-lg transition-all flex-1 md:flex-none ${
          !isApi
            ? 'bg-background text-foreground shadow-sm font-medium'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <span className="md:hidden">Локальные</span>
        <span className="hidden md:inline">Локальные настройки</span>
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-3 md:px-4 py-2 text-sm font-medium rounded-lg transition-all flex-1 md:flex-none ${
          isApi
            ? 'bg-background text-foreground shadow-sm font-medium'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <span className="md:hidden">API</span>
        <span className="hidden md:inline">API настройки</span>
      </button>
    </div>
  )
}

export default function DynamicTabBooster({ section, cfg, update, uploadBlock, isAdmin = false, campaignId }: Props) {
  const isApiMode = String(cfg['API_WORKER'] ?? '') === 'Да'
  const isMainSection = section === 'Основные настройки'

  const fields = getBoosterFieldsForSection(section, isAdmin)
  const visible = fields.filter(([key, def]) => {
    if (!isBoosterFieldVisible(key, cfg)) return false
    if (isMainSection && key === 'CNT_THREADS') return false
    if (isApiMode && isMainSection) {
      if (def.subsection && getApiHiddenSubsections(cfg).includes(def.subsection)) return false
      if (getApiHiddenFields(cfg).includes(key)) return false
    }
    return true
  })
  const threadsDef = isMainSection ? BOOSTER_RESOURCES['CNT_THREADS'] : undefined

  const hasSubsections = visible.some(([, def]) => def.subsection)

  const showApiDocs = isMainSection && isApiMode && String(cfg['API_MODE'] ?? '') === 'Личный API'
  const showSmartPilotInfo = section === 'Умный режим' && String(cfg['AIMODE_STATUS'] ?? '') !== 'Отключен' && String(cfg['AIMODE_STATUS'] ?? '') !== ''

  const autopilotOn = isAutopilotActive(cfg)
  const sectionHasAutopilotFields = fields.some(([, def]) => def.autopilotControl)
  const showAutopilotBanner = autopilotOn && sectionHasAutopilotFields

  const isProxySection = section === 'Прокси и анонимность'
  const proxyOn = String(cfg['PROXY_USE'] ?? '') === 'Да'

  // Bug #102: при включении «Лимитирование потоков по часам» (islimit=Да)
  // автозаполнить ВСЕ часы значением CNT_THREADS, чтобы клиент не
  // получил «копия остановилась в час с лимитом 0». Только если на
  // этот момент все limit_X_Y = 0 (фабричные дефолты), пользователь
  // не успел сам что-то проставить.
  useEffect(() => {
    if (cfg['islimit'] !== 'Да') return
    const threads = Number(cfg['CNT_THREADS']) || 1
    const allZero = Array.from({ length: 24 }, (_, h) => Number(cfg[`limit_${h}_${h + 1}`] ?? 0)).every(v => v === 0)
    if (!allZero) return
    const patch: Record<string, number> = {}
    for (let h = 0; h < 24; h++) patch[`limit_${h}_${h + 1}`] = threads
    update(patch as Partial<Settings>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg['islimit']])

  // Idea #83: при включении «Лимит кликов в час» подставить дефолтный
  // диапазон 30–50, если оператор ещё ничего не ввёл (иначе пустые поля
  // читаются как 0 = пауза весь день).
  useEffect(() => {
    if (cfg['CLICK_HOUR_LIMIT_STATUS'] !== 'Да') return
    const rawMin = cfg['CLICK_HOUR_LIMIT_MIN']
    const rawMax = cfg['CLICK_HOUR_LIMIT_MAX']
    const patch: Record<string, number> = {}
    if (rawMin === '' || rawMin === undefined || rawMin === null) patch['CLICK_HOUR_LIMIT_MIN'] = 30
    if (rawMax === '' || rawMax === undefined || rawMax === null) patch['CLICK_HOUR_LIMIT_MAX'] = 50
    if (Object.keys(patch).length) update(patch as Partial<Settings>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg['CLICK_HOUR_LIMIT_STATUS']])

  // Idea #83: при переключении на почасовую сетку, если все часы = 0
  // (фабричный дефолт), заполнить сеткой 40 кликов/час — оператору
  // остаётся лишь занизить ночные часы. 0 в часе = пауза этого часа.
  useEffect(() => {
    if (cfg['CLICK_HOUR_LIMIT_STATUS'] !== 'Да') return
    if (cfg['CLICK_HOUR_LIMIT_MODE'] !== 'Почасовая сетка') return
    const allZero = Array.from({ length: 24 }, (_, h) => Number(cfg[`click_limit_${h}_${h + 1}`] ?? 0)).every(v => v === 0)
    if (!allZero) return
    const patch: Record<string, number> = {}
    for (let h = 0; h < 24; h++) patch[`click_limit_${h}_${h + 1}`] = 40
    update(patch as Partial<Settings>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg['CLICK_HOUR_LIMIT_MODE'], cfg['CLICK_HOUR_LIMIT_STATUS']])

  return (
    <div className="animate-fade-in">
      <h2 className="text-lg font-semibold mb-5 text-foreground">{section}</h2>

      {isProxySection && proxyOn && (
        <ProxyPresetsPanel cfg={cfg} update={update} />
      )}

      {isMainSection && (
        <ConfigModeToggle
          isApi={isApiMode}
          onChange={(api) => update({ API_WORKER: api ? 'Да' : '' })}
        />
      )}

      {isMainSection && threadsDef && (
        <>
          <div className="flex flex-col items-stretch gap-2 py-3 px-4 rounded-lg transition-colors hover:bg-accent/50 md:flex-row md:items-center md:gap-3" style={{ overflow: 'visible' }}>
            <label className="w-full text-[13px] text-left shrink-0 pt-0 leading-snug font-medium flex items-center gap-1.5 text-foreground md:w-64">
              <span>{threadsDef.description}</span>
            </label>
            <div className="min-w-0 flex-1 w-full flex items-center gap-2 flex-wrap">
              <div className="min-w-0 flex-1 min-w-[120px]">
                <NumberStepper
                  def={threadsDef}
                  value={cfg['CNT_THREADS']}
                  onChange={v => update({ CNT_THREADS: v })}
                />
              </div>
              <OptimalThreadsButton campaignId={campaignId} onApply={v => update({ CNT_THREADS: v })} />
            </div>
          </div>
          {/* Bug #106: info-panel рендерится ОТДЕЛЬНО под row CNT_THREADS,
              чтобы не сдвигать кнопку и не накладываться на NumberStepper. */}
          <OptimalThreadsInfoPanel />
        </>
      )}

      {uploadBlock}

      {showApiDocs && <ApiDocumentation />}

      {showSmartPilotInfo && <SmartPilotInfo />}

      {showAutopilotBanner && (
        <div className="mb-5 flex items-start gap-3 px-4 py-3 rounded-lg border border-purple-500/20 bg-purple-500/5">
          <span className="text-lg mt-0.5">🧠</span>
          <div className="text-sm leading-relaxed">
            <span className="font-semibold text-purple-400">SmartPilot Автопилот</span>
            <span className="text-muted-foreground"> — часть настроек этого раздела скрыта, так как ими управляет автопилот: склик, DoubleClick, время на сайте, уникализация запросов, LSI.</span>
          </div>
        </div>
      )}

      <div className="space-y-2 md:space-y-1" style={{ overflow: 'visible' }}>
        {(() => {
          // Cross-subsection transitive grouping:
          // every visible field has a "root" — the topmost bool switch in its visibleWhen chain.
          // If rootOf(key) === key, it's a bool root (renders as card with children).
          // If rootOf(key) === null, it's standalone (no bool ancestor).
          // Else it's a child of some other bool — rendered inside that bool's card.
          const visibleMap = new Map<string, ResourceDef>()
          for (const [k, d] of visible) visibleMap.set(k, d)

          const rootOf = (key: string): string | null => {
            const seen = new Set<string>()
            let cur = key
            // eslint-disable-next-line no-constant-condition
            while (true) {
              if (seen.has(cur)) return null
              seen.add(cur)
              const d = visibleMap.get(cur)
              if (!d) return null
              const pvar = d.visibleWhen?.variable
              if (isSwitchResource(d)) {
                // Climb further ONLY if the parent is itself a switch (nested switches).
                // Otherwise this switch is the root — even if it depends on a plain Select.
                if (pvar) {
                  const p = visibleMap.get(pvar)
                  if (p && isSwitchResource(p)) { cur = pvar; continue }
                }
                return cur
              }
              if (pvar && visibleMap.has(pvar)) { cur = pvar; continue }
              return null
            }
          }

          // Determine subsection ownership: which root owns a subsection.
          // If all items of sub have the same root → header moves INSIDE that card.
          // Otherwise header stays outside.
          const subOwner = new Map<string | null, string | 'mixed' | null>()
          for (const [k, d] of visible) {
            const sub = d.subsection ?? null
            const r = rootOf(k)
            const cur = subOwner.get(sub)
            if (cur === undefined) subOwner.set(sub, r)
            else if (cur !== r) subOwner.set(sub, 'mixed')
          }

          const renderChildRow = (ckey: string, cdef: ResourceDef) => (
            <div key={ckey} className="flex flex-col gap-1.5 md:flex-row md:items-start md:gap-3 py-1.5">
              <label className="text-[12px] md:text-[13px] leading-snug font-medium text-foreground md:w-56 md:shrink-0 md:pt-2 flex items-center gap-1.5">
                <span>{cdef.description}</span>
                {cdef.hint && <HintTooltip text={cdef.hint} />}
              </label>
              <div className="min-w-0 flex-1">
                {cdef.type === 'RandomInteger' ? (
                  <RandomIntegerField name={ckey} def={cdef} cfg={cfg} update={update} />
                ) : (
                  <RenderField name={ckey} def={cdef} value={cfg[ckey]} onChange={v => update({ [ckey]: v })} isAdmin={isAdmin} />
                )}
                {ckey === 'API_WORKER_LINK' && String(cfg['API_WORKER_LINK'] ?? '').trim() !== '' && (
                  <ApiTestButton url={String(cfg['API_WORKER_LINK'] ?? '')} />
                )}
              </div>
            </div>
          )

          const renderNormalRow = (key: string, def: ResourceDef) => (
            <div
              key={key}
              className="flex flex-col items-stretch gap-2 py-3 px-4 rounded-lg transition-colors hover:bg-accent/50 md:flex-row md:items-start md:gap-3"
              style={{ overflow: 'visible' }}
            >
              <label className="w-full text-[13px] text-left shrink-0 pt-0 leading-snug font-medium flex items-center gap-1.5 text-foreground md:w-64 md:pt-2">
                <span className="min-w-0">{def.description}</span>
                {def.hint && <HintTooltip text={def.hint} />}
              </label>
              <div className="min-w-0 flex-1 w-full">
                {def.type === 'RandomInteger' ? (
                  <RandomIntegerField name={key} def={def} cfg={cfg} update={update} />
                ) : (
                  <RenderField name={key} def={def} value={cfg[key]} onChange={v => update({ [key]: v })} isAdmin={isAdmin} />
                )}
                {key === 'API_WORKER_LINK' && String(cfg['API_WORKER_LINK'] ?? '').trim() !== '' && (
                  <ApiTestButton url={String(cfg['API_WORKER_LINK'] ?? '')} />
                )}
              </div>
            </div>
          )

          const out: React.ReactNode[] = []
          const handled = new Set<string>()
          let externalSub: string | null | undefined = undefined

          for (const [key, def] of visible) {
            if (handled.has(key)) continue
            const root = rootOf(key)
            const sub = def.subsection ?? null
            const isSubExternal = subOwner.get(sub) === 'mixed' || subOwner.get(sub) == null

            // emit external subsection header once when it changes
            // — but only if this sub is external-owned (or mixed)
            const ownsSubInternal = (k: string | null): boolean => {
              const o = subOwner.get(k)
              return typeof o === 'string' && o !== 'mixed'
            }
            const shouldShowExternalHeader = hasSubsections && sub && (isSubExternal || (root === key))
            if (shouldShowExternalHeader && sub !== externalSub) {
              // Do not show header if it's owned by a different bool (only shown inside that bool)
              if (!ownsSubInternal(sub) || root === key) {
                out.push(<SubsectionHeader key={`sh-${sub}-${key}`} title={sub!} />)
                externalSub = sub
              }
            }

            if (root === key) {
              // Bool root — collect all children in visible order
              const kids: [string, ResourceDef][] = []
              for (const [ck, cd] of visible) {
                if (ck === key) continue
                if (rootOf(ck) === key) kids.push([ck, cd])
              }
              handled.add(key)
              for (const [ck] of kids) handled.add(ck)

              // Build inner content with subsection markers for subs owned by this bool
              const inner: React.ReactNode[] = []
              let innerSub: string | null | undefined = def.subsection ?? null
              for (const [ck, cd] of kids) {
                const csub = cd.subsection ?? null
                const owner = subOwner.get(csub)
                if (hasSubsections && csub && csub !== innerSub && owner === key) {
                  inner.push(<SubsectionHeader key={`isub-${csub}-${ck}`} title={csub} className="mt-4 mb-2" />)
                  innerSub = csub
                }
                inner.push(renderChildRow(ck, cd))
              }

              out.push(
                <BoolFieldRow
                  key={key}
                  def={def}
                  value={cfg[key]}
                  onChange={v => update({ [key]: v })}
                  hasChildren={kids.length > 0}
                >
                  {inner}
                </BoolFieldRow>
              )
              continue
            }

            if (root !== null) continue // child — handled by its root

            handled.add(key)
            if (isSwitchResource(def)) {
              out.push(
                <BoolFieldRow
                  key={key}
                  def={def}
                  value={cfg[key]}
                  onChange={v => update({ [key]: v })}
                  hasChildren={false}
                />
              )
            } else {
              out.push(renderNormalRow(key, def))
            }
          }
          return out
        })()}
      </div>

      {visible.length === 0 && (
        <div className="text-sm py-8 text-center rounded-lg border border-dashed border-border text-muted-foreground">
          Нет доступных полей для текущей конфигурации
        </div>
      )}

      {section === 'Основные настройки' && cfg['API_WORKER'] === 'Да' && (
        <div className="mt-6">
          <ApiLocalSection cfg={cfg} onChange={(k, v) => update({ [k]: v })} />
        </div>
      )}
    </div>
  )
}
